/**
 * The admin's register of hospitals, polyclinics and lab centres.
 *
 * Creating an institution creates two things at once: the institution and the
 * login that manages it. They are made in one transaction server-side, because
 * an institution nobody can sign into is unreachable through any screen and an
 * orphaned manager account cannot be cleaned up through any either.
 *
 * Quotas live here and only here. A manager can add doctors up to their
 * ceiling; nothing on their side of the app can raise it. That is the entire
 * commercial control the platform has over an institution, so it is deliberately
 * not delegated.
 *
 * Dashboard 1 of the upgrade — the Super Admin — adds three things per
 * hospital: a ceiling for each of the twelve staff roles inside the overall
 * one; "فتح لوحة المستشفى", which enters that hospital with platform authority
 * (every hospital screen then works inside it, see api/client.js
 * actingInstitution); and creating any of its staff directly, held to no quota.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatCard,
  Textarea,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { institutionApi, platformHospitalApi } from '../../api/endpoints';
import { actingInstitution } from '../../api/client';
import { MEMBER_ROLE_LABELS, SPEC_MEMBER_ROLES } from '../../domain/rbac';
import { StaffForm } from '../hospital/HospitalStaff';
import {
  INSTITUTION_TYPES,
  INSTITUTION_TYPE_LABELS,
  SERVICE_CATEGORY_LABELS,
  capabilitiesFor,
  quotaState,
} from '../../domain/institutions';

const blankStaff = () => ({
  memberRole: 'receptionist',
  name: '',
  username: '',
  password: '',
  email: '',
  phone: '',
  servesDoctorId: '',
  permissions: null,
  title: '',
  priceExam: '',
  priceFollowup: '',
  assignedRoomIds: [],
});

/**
 * One hospital's staff, from the platform side: the roster and its quota, and
 * a form that creates any of the twelve roles with no ceiling applied.
 */
function PlatformStaffModal({ institution, onClose, onChanged }) {
  const { success, error: toastError } = useNotifications();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await platformHospitalApi.members(institution.id));
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [institution.id]);

  useEffect(() => {
    load();
  }, [load]);

  const doctors = (data?.members || []).filter((m) => m.memberRole === 'doctor' && m.active);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.memberRole !== 'co_admin') delete payload.permissions;
      if (payload.memberRole !== 'secretary') delete payload.servesDoctorId;
      if (payload.memberRole !== 'doctor') {
        delete payload.title;
        delete payload.priceExam;
        delete payload.priceFollowup;
      }
      if (payload.memberRole !== 'nurse' || !payload.assignedRoomIds?.length) delete payload.assignedRoomIds;
      const created = await platformHospitalApi.createMember(institution.id, payload);
      success(`تم إنشاء حساب ${created.user?.name || ''} في ${institution.name}`);
      setForm(null);
      load();
      onChanged();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`فريق ${institution.name}`}
      icon="users"
      size="xl"
      footer={
        form ? (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setForm(null)}>
              رجوع
            </Button>
            <Button icon="user-plus" loading={saving} onClick={create}>
              إنشاء الحساب
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button icon="user-plus" onClick={() => setForm(blankStaff())}>
              حساب جديد (بلا حدود)
            </Button>
          </div>
        )
      }
    >
      {form ? (
        <StaffForm form={form} set={(patch) => setForm((f) => ({ ...f, ...patch }))} doctors={doctors} />
      ) : error ? (
        <ErrorState error={error} onRetry={load} />
      ) : !data ? (
        <Spinner label="جارٍ تحميل الفريق" variant="inline" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {SPEC_MEMBER_ROLES.map((role) => {
              const row = data.quota?.byRole?.[role] || { used: 0, max: null, remaining: 0 };
              return (
                <div key={role} className="rounded-xl border border-white/5 bg-white/[0.03] p-2 text-xs">
                  <p className="truncate font-bold text-white">{MEMBER_ROLE_LABELS[role]}</p>
                  <p className="text-slate-400">
                    {row.used}
                    {row.max !== null ? ` / ${row.max}` : ''} · متبقٍ للمستشفى {row.remaining}
                  </p>
                </div>
              );
            })}
          </div>
          {data.members.length === 0 ? (
            <EmptyState icon="users" title="لا يوجد موظفون بعد" />
          ) : (
            <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
              {data.members.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 p-3 text-sm">
                  <span className="font-bold text-white">{m.user?.name || '—'}</span>
                  <Badge className="border-white/10 bg-white/5 text-slate-300">{MEMBER_ROLE_LABELS[m.memberRole] || m.memberRole}</Badge>
                  {!m.active && <Badge className="border-red-500/30 bg-red-500/10 text-red-300">موقوف</Badge>}
                  <span className="ms-auto text-xs text-slate-500" dir="ltr">
                    {m.user?.username}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

const TYPE_ICON = {
  [INSTITUTION_TYPES.HOSPITAL]: 'building-2',
  [INSTITUTION_TYPES.POLYCLINIC]: 'stethoscope',
  [INSTITUTION_TYPES.LAB_CENTER]: 'flask-conical',
  [INSTITUTION_TYPES.PHARMACY]: 'pill',
};

const emptyForm = () => ({
  name: '',
  type: INSTITUTION_TYPES.HOSPITAL,
  city: '',
  address: '',
  phone: '',
  description: '',
  managerName: '',
  username: '',
  password: '',
  maxSubAccounts: 10,
  maxClinics: 20,
  maxServices: 50,
  roleQuotas: {},
  active: true,
  verified: false,
});

export function AdminInstitutions() {
  const { success, error: toastError } = useNotifications();
  const navigate = useNavigate();
  const [staffFor, setStaffFor] = useState(null);

  /** Enter a hospital with platform authority, and land on its staff screen. */
  const openHospital = (institution) => {
    actingInstitution.set({ id: institution.id, name: institution.name });
    success(`أنت الآن داخل ${institution.name}`);
    navigate('/hospital');
  };

  const state = useAsync(() => institutionApi.list(), [], { initialData: [] });
  const institutions = useMemo(() => state.data || [], [state.data]);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const totals = useMemo(
    () => ({
      all: institutions.length,
      active: institutions.filter((i) => i.active).length,
      members: institutions.reduce((sum, i) => sum + (i.usage?.members || 0), 0),
      services: institutions.reduce((sum, i) => sum + (i.usage?.services || 0), 0),
    }),
    [institutions],
  );

  const handleCreate = async (form) => {
    const created = await institutionApi.create(form);
    success(`تم إنشاء ${form.name} وحساب المدير`);
    setCreating(false);
    state.reload();
    return created;
  };

  const handleUpdate = async (form) => {
    await institutionApi.update(editing.id, form);
    success('تم حفظ التعديلات');
    setEditing(null);
    state.reload();
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      const result = await institutionApi.remove(removing.id);
      success(result.deactivated ? 'تم تعطيل المنشأة' : 'تم حذف المنشأة');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر الحذف');
    } finally {
      setRemoving(null);
    }
  };

  const toggleActive = async (institution) => {
    try {
      await institutionApi.update(institution.id, { active: !institution.active });
      success(institution.active ? 'تم إيقاف المنشأة' : 'تم تفعيل المنشأة');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر التحديث');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="المنشآت الطبية"
        subtitle={`${totals.all} منشأة — ${totals.active} مفعّلة`}
        icon="building-2"
        actions={
          <>
            <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
              تحديث
            </Button>
            <Button icon="plus" onClick={() => setCreating(true)}>
              إضافة منشأة
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="building-2" label="إجمالي المنشآت" value={totals.all} accent="cyan" />
        <StatCard icon="check-circle-2" label="مفعّلة" value={totals.active} accent="emerald" />
        <StatCard icon="users" label="حسابات فرعية" value={totals.members} accent="purple" />
        <StatCard icon="package" label="خدمات معروضة" value={totals.services} accent="amber" />
      </div>

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && institutions.length === 0 ? (
        <Spinner label="جاري تحميل المنشآت..." />
      ) : institutions.length === 0 ? (
        <Panel>
          <EmptyState
            icon="building-2"
            title="لا توجد منشآت"
            message="أضف مستشفى أو مركزاً طبياً ليبدأ في عرض خدماته للمرضى."
            action={
              <Button icon="plus" onClick={() => setCreating(true)}>
                إضافة منشأة
              </Button>
            }
          />
        </Panel>
      ) : (
        /* grid-cols-1, not bare grid: an implicit track is auto-sized, so one
            long unbreakable name (a hospital's, a username, an email) widens
            it past the viewport. grid-cols-1 is minmax(0, 1fr), which caps
            the track at the container and lets the truncation inside work. */
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {institutions.map((institution) => (
            <InstitutionCard
              key={institution.id}
              institution={institution}
              onEdit={() => setEditing(institution)}
              onToggle={() => toggleActive(institution)}
              onRemove={() => setRemoving(institution)}
              onOpen={() => openHospital(institution)}
              onStaff={() => setStaffFor(institution)}
            />
          ))}
        </div>
      )}

      {staffFor && <PlatformStaffModal institution={staffFor} onClose={() => setStaffFor(null)} onChanged={state.reload} />}

      <InstitutionFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={handleCreate}
        mode="create"
      />

      <InstitutionFormModal
        open={Boolean(editing)}
        institution={editing}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdate}
        mode="edit"
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
        title="حذف المنشأة"
        confirmLabel="حذف"
        message={`سيتم حذف ${removing?.name || 'المنشأة'}. إذا كان لها حجوزات سابقة فسيتم تعطيلها بدلاً من حذفها للحفاظ على سجلات المرضى.`}
      />
    </div>
  );
}

/* ========================================================================== */

function InstitutionCard({ institution, onEdit, onToggle, onRemove, onOpen, onStaff }) {
  const members = quotaState({ used: institution.usage?.members || 0, limit: institution.maxSubAccounts });
  const services = quotaState({ used: institution.usage?.services || 0, limit: institution.maxServices });
  const offers = capabilitiesFor(institution.type);

  return (
    <Panel className="space-y-4">
      <div className="flex items-start gap-3">
        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
          style={{
            color: institution.color || '#06b6d4',
            background: `${institution.color || '#06b6d4'}22`,
          }}
        >
          <Icon name={TYPE_ICON[institution.type] || 'building-2'} size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-bold text-white">{institution.name}</h3>
          <p className="truncate text-xs text-slate-400">
            {INSTITUTION_TYPE_LABELS[institution.type] || institution.type}
            {institution.city ? ` — ${institution.city}` : ''}
          </p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge
              className={
                institution.active
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                  : 'border-slate-600 bg-slate-800 text-slate-400'
              }
            >
              {institution.active ? 'مفعّلة' : 'موقوفة'}
            </Badge>
            {institution.verified && (
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="check-circle-2">
                موثّقة
              </Badge>
            )}
            {/* A username is one unbreakable word; on a phone it must shorten
                inside the card rather than push the whole page sideways. */}
            {institution.manager && (
              <Badge className="min-w-0 max-w-full border-white/10 bg-white/5 text-slate-300" icon="user">
                <span className="min-w-0 truncate" dir="ltr" title={institution.manager.username}>
                  {institution.manager.username}
                </span>
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Quota bars — the number an admin is actually managing */}
      <div className="grid gap-2 sm:grid-cols-2">
        <QuotaBar label="الحسابات الفرعية" quota={members} icon="users" />
        <QuotaBar label="الخدمات" quota={services} icon="package" />
      </div>

      {offers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {offers.map((category) => (
            <span
              key={category}
              className="rounded-lg bg-white/5 px-2 py-1 text-[10px] font-bold text-slate-300"
            >
              {SERVICE_CATEGORY_LABELS[category]}
            </span>
          ))}
        </div>
      )}

      {/* The roles with a ceiling of their own, used against it. */}
      {institution.roleQuotas && Object.keys(institution.roleQuotas).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(institution.roleQuotas).map(([role, max]) => (
            <span key={role} className="rounded-lg bg-purple-500/10 px-2 py-1 text-[10px] font-bold text-purple-200">
              {MEMBER_ROLE_LABELS[role] || role}: {institution.quota?.byRole?.[role]?.used ?? 0}/{max}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-white/5 pt-3">
        <Button icon="log-in" onClick={onOpen}>
          فتح لوحة المستشفى
        </Button>
        <Button variant="ghost" icon="users" onClick={onStaff}>
          الفريق
        </Button>
        <Button variant="subtle" icon="trash-2" className="!text-red-300" onClick={onRemove}>
          حذف
        </Button>
        <Button variant="subtle" icon={institution.active ? 'pause' : 'play'} onClick={onToggle}>
          {institution.active ? 'إيقاف' : 'تفعيل'}
        </Button>
        <Button variant="ghost" icon="pencil" onClick={onEdit}>
          تعديل
        </Button>
      </div>
    </Panel>
  );
}

/**
 * Usage against a ceiling.
 *
 * Amber at the limit and red past it — an admin who lowers a quota under
 * accounts that already exist needs to see that they have, because nothing
 * removes the surplus automatically.
 */
function QuotaBar({ label, quota, icon }) {
  const tone = quota.exceeded ? '#f87171' : quota.full ? '#fbbf24' : '#34d399';

  return (
    <div className="rounded-xl border border-white/5 bg-white/5 p-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[10px] text-slate-400">
          <Icon name={icon} size={11} style={{ color: tone }} />
          {label}
        </span>
        <span className="text-[11px] font-bold" style={{ color: tone }}>
          {quota.used} / {quota.limit}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${quota.percent}%`, background: tone }}
        />
      </div>
    </div>
  );
}

/* ========================================================================== */

function InstitutionFormModal({ open, institution, onClose, onSubmit, mode }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(
      institution ? { ...emptyForm(), ...institution, roleQuotas: { ...(institution.roleQuotas || {}) } } : emptyForm(),
    );
  }, [open, institution]);

  if (!open) return null;

  const update = (field) => (event) => {
    const target = event.target;
    setForm((current) => ({
      ...current,
      [field]: target.type === 'checkbox' ? target.checked : target.value,
    }));
    setError(null);
  };

  const creating = mode === 'create';
  const capabilities = capabilitiesFor(form.type);

  const submit = async () => {
    if (!String(form.name).trim()) return setError('اسم المنشأة مطلوب');
    if (creating) {
      if (!String(form.username).trim()) return setError('اسم المستخدم للمدير مطلوب');
      if (String(form.password).length < 6) return setError('كلمة المرور 6 أحرف على الأقل');
    }

    setSaving(true);
    try {
      await onSubmit({
        ...form,
        maxSubAccounts: Number(form.maxSubAccounts) || 0,
        maxClinics: Number(form.maxClinics) || 0,
        maxServices: Number(form.maxServices) || 0,
        // An empty box is "no ceiling of its own"; the server drops it.
        roleQuotas: Object.fromEntries(
          Object.entries(form.roleQuotas || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined),
        ),
      });
    } catch (err) {
      setError(err.message || 'تعذر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={creating ? 'إضافة منشأة طبية' : `تعديل ${institution?.name || ''}`}
      icon="building-2"
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          {error && <span className="text-xs font-bold text-red-400">{error}</span>}
          <div className="mr-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button icon="save" loading={saving} onClick={submit}>
              {creating ? 'إنشاء المنشأة' : 'حفظ'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="space-y-4">
          <h4 className="text-sm font-bold text-cyan-300">بيانات المنشأة</h4>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="اسم المنشأة" required>
              <Input value={form.name} onChange={update('name')} autoFocus />
            </Field>

            <Field
              label="النوع"
              required
              hint={creating ? undefined : 'لا يمكن تغييره بعد إضافة خدمات'}
            >
              <Select value={form.type} onChange={update('type')}>
                {Object.values(INSTITUTION_TYPES).map((type) => (
                  <option key={type} value={type}>
                    {INSTITUTION_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {/* What this type may sell — set by the platform, not by the admin.
              Shown so the choice of type is made against its consequence. */}
          <div className="rounded-xl border border-white/5 bg-white/5 p-3">
            <p className="mb-1.5 text-[11px] font-bold text-slate-300">الخدمات المسموح بها لهذا النوع</p>
            {capabilities.length === 0 ? (
              <p className="text-[11px] text-slate-500">
                كشوفات ومتابعات فقط — لا تعرض خدمات إضافية على شاشة المريض.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {capabilities.map((category) => (
                  <span
                    key={category}
                    className="rounded-lg bg-cyan-500/10 px-2 py-1 text-[10px] font-bold text-cyan-300"
                  >
                    {SERVICE_CATEGORY_LABELS[category]}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="المدينة">
              <Input value={form.city || ''} onChange={update('city')} />
            </Field>
            <Field label="الهاتف">
              <Input value={form.phone || ''} onChange={update('phone')} dir="ltr" />
            </Field>
          </div>

          <Field label="العنوان">
            <Input value={form.address || ''} onChange={update('address')} />
          </Field>

          <Field label="نبذة">
            <Textarea value={form.description || ''} onChange={update('description')} rows={2} />
          </Field>
        </section>

        {creating && (
          <section className="space-y-4 border-t border-white/10 pt-5">
            <h4 className="text-sm font-bold text-cyan-300">حساب المدير</h4>
            <p className="text-[11px] leading-relaxed text-slate-500">
              يُنشأ حساب واحد لمدير المنشأة. منه يضيف الأطباء والخدمات ويتابع الإيرادات.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="اسم المدير">
                <Input value={form.managerName} onChange={update('managerName')} />
              </Field>
              <Field label="اسم المستخدم" required>
                <Input value={form.username} onChange={update('username')} dir="ltr" />
              </Field>
            </div>

            <Field label="كلمة المرور" required hint="6 أحرف على الأقل">
              <Input
                type="password"
                value={form.password}
                onChange={update('password')}
                dir="ltr"
              />
            </Field>
          </section>
        )}

        <section className="space-y-4 border-t border-white/10 pt-5">
          <h4 className="text-sm font-bold text-cyan-300">الحدود المسموح بها</h4>
          <p className="text-[11px] leading-relaxed text-slate-500">
            المدير يضيف حتى هذه الأعداد بنفسه. لا يمكنه رفعها — تُغيَّر من هنا فقط.
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="حسابات فرعية">
              <Input type="number" min="0" value={form.maxSubAccounts} onChange={update('maxSubAccounts')} />
            </Field>
            <Field label="عيادات">
              <Input type="number" min="0" value={form.maxClinics} onChange={update('maxClinics')} />
            </Field>
            <Field label="خدمات">
              <Input type="number" min="0" value={form.maxServices} onChange={update('maxServices')} />
            </Field>
          </div>

          {form.type === INSTITUTION_TYPES.HOSPITAL && (
            <div className="rounded-xl border border-purple-500/20 bg-purple-500/[0.05] p-3">
              <p className="mb-1 text-[11px] font-bold text-purple-200">حد كل دور داخل الحسابات الفرعية</p>
              <p className="mb-3 text-[11px] text-slate-500">
                اتركه فارغاً ليكون الدور محدوداً بإجمالي الحسابات الفرعية فقط. يرى مدير المستشفى ما أُنشئ وما تبقى لكل دور.
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {SPEC_MEMBER_ROLES.map((role) => (
                  <label key={role} className="text-[11px] text-slate-300">
                    {MEMBER_ROLE_LABELS[role]}
                    <Input
                      type="number"
                      min="0"
                      placeholder="بلا حد"
                      value={form.roleQuotas?.[role] ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, roleQuotas: { ...(f.roleQuotas || {}), [role]: e.target.value } }))
                      }
                      className="mt-1"
                      aria-label={`حد ${MEMBER_ROLE_LABELS[role]}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={Boolean(form.active)} onChange={update('active')} />
              مفعّلة (تظهر للمرضى)
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={Boolean(form.verified)} onChange={update('verified')} />
              موثّقة
            </label>
          </div>
        </section>
      </div>
    </Modal>
  );
}

export default AdminInstitutions;
